import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { reportSettingsTemplateContract } from "@common/contracts/report-settings-template.contract";
import {
    ReportSettingsTemplateCreateSchema,
    ReportSettingsTemplateUpdateSchema,
} from "@common/schemas/domains/reportSettings/report-settings-template.schema";
import { ReportSettingsTemplateApply } from "@common/schemas/domains/reportSettings/report-settings-template-application.schema";
import {
    SaveTemplateReportSettingsBody,
    TemplateReportSettingsResponse,
} from "@common/schemas/domains/reportSettings/report-settings-template-settings.schema";
import { reportSettingsQueryKeys } from "@api/reportSettings.api";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { useScope } from "@state/scopeContext.lib";

const reportSettingsTemplateClient = getBackendAPIClient(
    reportSettingsTemplateContract,
);

type CreateReportTemplateBody = z.infer<
    typeof ReportSettingsTemplateCreateSchema
>;

type UpdateReportTemplateBody = z.infer<
    typeof ReportSettingsTemplateUpdateSchema
>;

const reportTemplateQueryKeys = {
    list: (organizationId: string) =>
        ["report-settings-templates", organizationId] as const,
    settings: ({
        organizationId,
        templateId,
    }: {
        organizationId: string | null | undefined;
        templateId: string;
    }) =>
        [
            "report-settings-templates",
            organizationId,
            templateId,
            "settings",
        ] as const,
    targets: (organizationId: string) =>
        ["report-settings-templates", organizationId, "targets"] as const,
};

export function useReportTemplates() {
    const { organizationId } = useScope();

    return useQuery({
        queryKey: organizationId
            ? reportTemplateQueryKeys.list(organizationId)
            : [],
        queryFn: async () => {
            if (!organizationId) {
                return [];
            }
            const response =
                await reportSettingsTemplateClient.listReportSettingsTemplates({
                    params: { organizationId },
                });
            return response.body.reportSettingsTemplates;
        },
        enabled: !!organizationId,
    });
}

const useInvalidateReportTemplates = () => {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return () => {
        if (!organizationId) {
            return;
        }
        void queryClient.invalidateQueries({
            queryKey: reportTemplateQueryKeys.list(organizationId),
        });
    };
};

export function useCreateReportTemplateMutation() {
    const { organizationId } = useScope();
    const invalidateReportTemplates = useInvalidateReportTemplates();

    return useMutation({
        mutationFn: async (body: CreateReportTemplateBody) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }
            const response =
                await reportSettingsTemplateClient.createReportSettingsTemplate(
                    {
                        params: { organizationId },
                        body,
                        extraHeaders: {
                            [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                        },
                    },
                );
            return response.body;
        },
        onSuccess: invalidateReportTemplates,
    });
}

export function useUpdateReportTemplateMutation() {
    const { organizationId } = useScope();
    const invalidateReportTemplates = useInvalidateReportTemplates();

    return useMutation({
        mutationFn: async ({
            templateId,
            body,
        }: {
            templateId: string;
            body: UpdateReportTemplateBody;
        }) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }
            const response =
                await reportSettingsTemplateClient.updateReportSettingsTemplate(
                    {
                        params: {
                            organizationId,
                            reportSettingsTemplateId: templateId,
                        },
                        body,
                        extraHeaders: {
                            [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                        },
                    },
                );
            return response.body;
        },
        onSuccess: invalidateReportTemplates,
    });
}

type TemplateIdRequest = (args: {
    params: { organizationId: string; reportSettingsTemplateId: string };
}) => Promise<object>;

const useTemplateIdMutation = (request: TemplateIdRequest) => {
    const { organizationId } = useScope();
    const invalidateReportTemplates = useInvalidateReportTemplates();

    return useMutation({
        mutationFn: async (templateId: string) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }
            await request({
                params: {
                    organizationId,
                    reportSettingsTemplateId: templateId,
                },
            });
        },
        onSuccess: invalidateReportTemplates,
    });
};

export const useArchiveReportTemplateMutation = () =>
    useTemplateIdMutation(
        reportSettingsTemplateClient.archiveReportSettingsTemplate,
    );

export const useUnarchiveReportTemplateMutation = () =>
    useTemplateIdMutation(
        reportSettingsTemplateClient.unarchiveReportSettingsTemplate,
    );

export const useDeleteReportTemplateMutation = () =>
    useTemplateIdMutation(
        reportSettingsTemplateClient.deleteReportSettingsTemplate,
    );

export function useSetDefaultReportTemplateMutation() {
    const { organizationId } = useScope();
    const invalidateReportTemplates = useInvalidateReportTemplates();

    return useMutation({
        mutationFn: async (templateId: string | null) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }
            await reportSettingsTemplateClient.setDefaultReportSettingsTemplate(
                {
                    params: { organizationId },
                    body: { templateId },
                },
            );
        },
        onSuccess: invalidateReportTemplates,
    });
}

export function useReportTemplateSettings(templateId: string) {
    const { organizationId } = useScope();

    return useQuery({
        queryKey: reportTemplateQueryKeys.settings({
            organizationId,
            templateId,
        }),
        queryFn: async (): Promise<TemplateReportSettingsResponse> => {
            if (!organizationId) {
                throw new Error("No active organization");
            }
            const response =
                await reportSettingsTemplateClient.getReportSettingsTemplateSettings(
                    {
                        params: {
                            organizationId,
                            reportSettingsTemplateId: templateId,
                        },
                    },
                );
            if (response.status !== 200) {
                throw new Error("Failed to load report settings");
            }
            return response.body;
        },
        refetchOnWindowFocus: false,
        enabled: !!organizationId,
    });
}

export function useSaveReportTemplateSettingsMutation(templateId: string) {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (
            body: SaveTemplateReportSettingsBody,
        ): Promise<void> => {
            if (!organizationId) {
                throw new Error("No active organization");
            }
            const response =
                await reportSettingsTemplateClient.updateReportSettingsTemplateSettings(
                    {
                        params: {
                            organizationId,
                            reportSettingsTemplateId: templateId,
                        },
                        body,
                    },
                );
            if (response.status !== 200) {
                throw new Error("Failed to save report settings");
            }
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: reportTemplateQueryKeys.settings({
                        organizationId,
                        templateId,
                    }),
                }),
                queryClient.invalidateQueries({
                    queryKey: reportSettingsQueryKeys.allWorkspaces(),
                }),
            ]);
        },
    });
}

export function useReportTemplateTargets({
    isEnabled,
}: {
    isEnabled: boolean;
}) {
    const { organizationId } = useScope();

    return useQuery({
        queryKey: organizationId
            ? reportTemplateQueryKeys.targets(organizationId)
            : [],
        queryFn: async () => {
            if (!organizationId) {
                throw new Error("No active organization");
            }
            const response =
                await reportSettingsTemplateClient.getReportSettingsTemplateTargets(
                    { params: { organizationId } },
                );
            if (response.status !== 200) {
                throw new Error("Failed to load report template targets");
            }
            return response.body;
        },
        enabled: isEnabled && !!organizationId,
        refetchOnWindowFocus: false,
    });
}

export function useApplyReportTemplateMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (body: ReportSettingsTemplateApply) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }
            const response =
                await reportSettingsTemplateClient.applyReportSettingsTemplate({
                    params: { organizationId },
                    body,
                    extraHeaders: {
                        [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                    },
                });
            if (response.status !== 200) {
                throw new Error("Failed to apply report template");
            }
        },
        onSuccess: async () => {
            if (!organizationId) {
                return;
            }
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: reportTemplateQueryKeys.list(organizationId),
                }),
                queryClient.invalidateQueries({
                    queryKey: reportTemplateQueryKeys.targets(organizationId),
                }),
                queryClient.invalidateQueries({
                    queryKey: reportSettingsQueryKeys.allWorkspaces(),
                }),
            ]);
        },
    });
}
